/**
 * PartPicker — parts as a grouped list (plan 3.16): the whole interior (the cabin has no drawing yet) and the
 * few exterior parts that are not on the map. Each group of the API's part list is a heading with a row per
 * part showing its current fault's severity. Pure presentation like `ExteriorMap`: the modal owns the faults
 * and the selection, and the parts keep the same `dil-<id>` test ids so either view is addressed the same way.
 */

import type { Dil, Zavaznost } from './api';
import { SEVERITY_FILL, ZAVAZNOST_TEXT } from './ExteriorMap';

interface PartPickerProps {
  /** Already filtered by the caller to what this list shows. */
  parts: readonly Dil[];
  /** Heading for parts without a cabin group. */
  fallbackGroup?: string;
  testId?: string;
  faults: ReadonlyMap<number, Zavaznost>;
  selected: number | null;
  onSelect: (id: number) => void;
  /** Frozen (a save is in flight): the selection must not move under the payload. */
  disabled?: boolean;
}

export function PartPicker({
  parts,
  fallbackGroup = 'Ostatní',
  testId = 'zavady-dily',
  faults,
  selected,
  onSelect,
  disabled = false,
}: PartPickerProps) {
  const groups: { skupina: string; parts: Dil[] }[] = [];
  for (const p of parts) {
    const name = p.skupina ?? fallbackGroup;
    let g = groups.find((x) => x.skupina === name);
    if (!g) groups.push((g = { skupina: name, parts: [] }));
    g.parts.push(p);
  }

  return (
    <div data-testid={testId} className="flex min-w-0 flex-col gap-4">
      {groups.map((g) => (
        <section key={g.skupina}>
          <h3 className="mb-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">{g.skupina}</h3>
          <ul className="border border-line">
            {g.parts.map((p) => {
              const severity = faults.get(p.id_dilu);
              return (
                <li key={p.id_dilu} className="border-b border-line last:border-b-0">
                  <button
                    type="button"
                    disabled={disabled}
                    data-testid={`dil-${p.id_dilu}`}
                    data-zavaznost={severity ?? 'none'}
                    aria-pressed={selected === p.id_dilu}
                    onClick={() => onSelect(p.id_dilu)}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left text-[13px] text-ink hover:bg-accent-soft disabled:cursor-not-allowed ${
                      selected === p.id_dilu ? 'bg-accent-soft' : ''
                    }`}
                  >
                    {/* the swatch is the exterior map's own fill, so both views read the same */}
                    <svg viewBox="0 0 10 10" className="size-3 shrink-0" aria-hidden>
                      <rect x="0.5" y="0.5" width="9" height="9" strokeWidth="1" className={SEVERITY_FILL[severity ?? 'none']} />
                    </svg>
                    <span className="min-w-0 flex-1 truncate">{p.nazev}</span>
                    {severity && <span className="shrink-0 text-[12px] text-ink-2">{ZAVAZNOST_TEXT[severity]}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
